import React from 'react'
import { connect } from 'react-redux';
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Types
import { ElementSize } from '../../types/element-size';

// Constants
import { EMAIL_LENGTH_LIMITS } from '../../constants/common';

// Components
import InputControl from '../InputControl/InputControl';
import IconEnvelope from '../Icons/IconEnvelope/IconEnvelope';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../InteractiveButton/InteractiveButton';

type EmailVerifyFormProps = {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  email: string | null,
} & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const EmailVerifyForm: React.FC<EmailVerifyFormProps> = (props) => {
  const { checkAuthFlow, email, onChange, isAuthLoading } = props;

  const loadingClassName = isAuthLoading ? 'flex-auto justify-center items-center' : 'flex-col w-full';
  const emailVerifyClassName = `c-email-verify-form flex  ${loadingClassName}`;

  // Logic
  const formik = useFormik({
    initialValues: {
      email: email || '',
    },
    validationSchema: object({
      email: string()
        .email('Invalid email format')
        .min(EMAIL_LENGTH_LIMITS.MIN, `Minimum ${EMAIL_LENGTH_LIMITS.MIN} characters required`)
        .max(EMAIL_LENGTH_LIMITS.MAX, `Maximum ${EMAIL_LENGTH_LIMITS.MAX} characters required`)
        .required('Required'),
    }),
    onSubmit: (values) => {
      const customEvent = {
        target: {
          name: 'email',
          value: values.email,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(customEvent);
      checkAuthFlow(values.email);
    },
  });

  const isDisabledBtn = Boolean(formik.touched.email && formik.errors.email);

  return (
    <div className={emailVerifyClassName}>
      {!isAuthLoading ? (
        <form className='flex-auto' onSubmit={formik.handleSubmit}>
          <div className="pb-3">
            <InputControl
              label='Email'
              type="email"
              name='email'
              placeholder="Enter your email"
              value={formik.values.email}
              customOnChange={formik.handleChange}
              iconRight={<IconEnvelope size={ElementSize.MD} />}
              onBlur={formik.handleBlur}
              errorText={formik.errors.email}
              showError={formik.touched.email && formik.errors.email}
            />
          </div>
          <InteractiveButton
            text='Please Log In'
            submit
            disabled={isDisabledBtn}
            size={ElementSize.XL}
            className="w-full"
          />
        </form>
      ) : (
        <div className='flex-auto flex justify-center items-center'>
          <SpinnerLoader />
        </div>
      )}
    </div>
  )
}

const mapState = (state: RootState) => ({
  isAuthLoading: state.auth.isAuthLoading,
});

const mapDispatch = (dispatch: Dispatch) => ({
  checkAuthFlow: dispatch.auth.checkAuthFlow,
});

export default connect(mapState, mapDispatch)(EmailVerifyForm);
